import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { lockScroll, unlockScroll } from '@/utils/responsive';
import { useIsMobile } from '@/hooks';

/**
 * ResponsiveModal - Mobile-responsive modal wrapper
 * 
 * Desktop: Standard modal with max-width
 * Mobile: Full-screen modal with sticky header/footer
 * 
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {string} title - Modal title
 * @param {React.ReactNode} children - Modal content
 * @param {React.ReactNode} footer - Optional footer content
 * @param {string} size - Modal size: 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {boolean} showCloseButton - Show X button (default: true)
 */
const ResponsiveModal = ({ 
  isOpen, 
  onClose, 
  title,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  closeOnOutsideClick = true,
  className = '',
  ...props
}) => {
  const isMobile = useIsMobile();

  // Lock scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => unlockScroll();
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'md:max-w-md',
    md: 'md:max-w-2xl',
    lg: 'md:max-w-4xl',
    xl: 'md:max-w-6xl',
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-4"
      {...props}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={closeOnOutsideClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div 
        className={`
          relative bg-white
          w-full h-full md:h-auto
          md:max-h-[90vh] ${sizeClasses[size]}
          md:rounded-lg
          shadow-xl
          flex flex-col
          ${className}
        `}
      >
        {/* Header - Sticky on mobile */}
        {(title || showCloseButton) && (
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 md:px-6 md:py-4 flex items-center justify-between">
            {title && (
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 truncate pr-4">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 md:w-6 md:h-6 text-gray-500" />
              </button>
            )}
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6">
          {children}
        </div>

        {/* Footer - Sticky on mobile */}
        {footer && (
          <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 px-4 py-3 md:px-6">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponsiveModal;

